<template>
  <main class="guide">
    <div class="container">
      <!-- Hero -->
      <section class="hero">
        <div class="hero__content">
          <h1>How to use the Analysis Tool</h1>
          <p class="sub">
            Follow funds from a specific blockchain transaction to see where they went — exchanges, idle addresses, or risk endpoints.
          </p>
          <div class="cta">
            <router-link class="btn btn--primary" to="/AnalysisTool">Open the Analysis Tool</router-link>
         <!--  <router-link class="btn btn--ghost" to="/support">Need help?</router-link>--> 
          </div>
        </div>
        <div class="hero__art" aria-hidden="true"></div>
      </section>

      <!-- Quick steps -->
      <section class="grid">
        <article class="card">
          <h3>Step 1: Enter the Transaction Hash</h3>
          <p>
            The <strong>transaction hash</strong> is a unique ID (like a receipt number). You’ll find it in:
          </p>
          <ul class="list">
            <li>Your exchange account’s transaction history (e.g., Coinbase, Binance)</li>
            <li>Your wallet app or explorer (e.g., MetaMask, Ledger, block explorers)</li>
          </ul>
         <!--<p class="callout callout--warn">Do <strong>not</strong> enter the destination address by itself.</p>--> 
          <p class="callout callout--tip">
            Ethereum hashes usually start with <code>0x</code>, don't omit the 0x.
          </p>
          <div class="samples">
            <div><span class="label">ETH:</span> <code>0xb4560b60b6fcb6721d7e9325f2279c663d2e4c29bd4c964708443bbbf78400d8</code></div>
            <div><span class="label">BTC:</span> <code>71a6c97731d4700307bee56aab7bfb3e781f95c889cd54d9a73d9ef510f1b29a</code></div>
          </div>
        </article>

        <article class="card">
          <h3>Step 2 (optional): “To Address”</h3>
          <p>
            If you know the exact <strong>recipient address</strong>, paste it. If it’s required and you don’t provide it,
            the tool will show likely options to choose from.
          </p>
        <!--  <p class="callout callout--warn">Picking the wrong recipient will follow the wrong path.</p>-->
        </article>

        <article class="card">
          <h3>Step 3: Submit & Process</h3>
          <p>
            Click <strong>Submit Request</strong>. Jobs are queued <strong>first‑come, first‑served</strong> under fair use.
          </p>
          <p class="callout callout--tip">Most requests finish in under 10 minutes. If it takes longer, report a bug from the result row.</p>
        </article>
      </section>

      <!-- Where to find hashes: exchange walkthrough -->
      <section class="card">
        <h2>Finding your transaction hash (exchange accounts)</h2>
        <p>
          If you sent funds from an exchange, the hash is almost always shown on the transaction’s detail page. You’re usually
          looking for an <strong>outgoing</strong> transfer from your account to a wallet or another platform.
        </p>
        <ol class="steps">
          <li>Open your exchange and go to <strong>History</strong> or <strong>Transactions</strong>.</li>
          <li>Filter for <strong>Withdrawals</strong> (or “Sent” / “Transfers out”).</li>
          <li>Click the transaction to open its details.</li>
          <li>Look for <strong>Tx Hash</strong>, <strong>Transaction ID</strong>, or a link to a block explorer
              (e.g., Etherscan, Tronscan, Blockchain.com).</li>
          <li>Copy the full hash and paste it into the tool.</li>
        </ol>
        <p class="muted">
          Tip: On some exchanges the hash is hidden behind a <em>View on explorer</em> link — open it, then copy the
          <strong>transaction hash</strong> from the explorer page.
        </p>
      </section>

      <!-- Results -->
      <section class="card">
        <h2>What you’ll get</h2>
        <ul class="list">
          <li>A concise, readable <strong>text summary</strong> of where funds went</li>
          <li>A <strong>visual screenshot</strong> of the transaction path (use “View Screenshot”)</li>
        </ul>
        <p class="muted">In Normal mode, you receive the summary and the visualization.</p>
      </section>

      <!-- Flags -->
      <section class="card">
        <h2>Flags and meanings</h2>
        <ul class="flags">
          <li>
            <img src="../assets/greenflag.png" alt="" class="flag" />
            <strong>Green:</strong> Likely compliant exchange destination
          </li>
          <li>
            <img src="../assets/blueflag.png" alt="" class="flag" />
            <strong>Blue:</strong> Funds currently remain at that address
          </li>
          <li>
            <img src="../assets/orangeflag.png" alt="" class="flag" />
            <strong>Orange:</strong> Complication detected — manual review recommended
          </li>
          <li>
            <img src="../assets/redflag.png" alt="" class="flag" />
            <strong>Red:</strong> Non‑compliant or gambling destination
          </li>
        </ul>
      </section>

      <!-- Fair use -->
      <section class="card">
        <h2>Fair use limits</h2>
        <ul class="list">
          <li>🔁 Don’t submit duplicate requests — delete old ones first.</li>
          <li>📦 Max <strong>150</strong> saved requests (delete older ones to free space).</li>
          <li>⏳ Max <strong>30</strong> pending at a time.</li>
          <li>❌ Unsupported networks may fail and still consume a credit.</li>
        </ul>
      </section>

      <!-- Bugs -->
      <section class="card">
        <h2>Bug reports & refunds</h2>
        <p>
          If a request is stuck or fails, use <strong>Report Bug</strong> beside that row. Admins review a few times per week —
          valid reports are eligible for credit refunds.
        </p>
      </section>

      <section class="card card--cta">
        <div class="card__row">
          <div>
            <h3>Ready to trace?</h3>
            <p class="muted">Start with your transaction hash. If you hit a snag, we’ll help.</p>
          </div>
          <router-link class="btn btn--primary" to="/AnalysisTool">Open the Analysis Tool</router-link>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ToolGuide',
  mounted() {
    document.body.classList.add('route-guide');   // unify bg + keep nav above
  },
  beforeUnmount() {
    document.body.classList.remove('route-guide');
  }
}
</script>

<style scoped>
/* Page tokens consistent with your new light theme */
.guide {
  --bg: #f7f9fc;
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 1.25rem 1rem 2.5rem;
  background: var(--bg);
}
.container { max-width: 1120px; margin: 0 auto; }

/* HERO */
.hero {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff, #f3f6fb);
  border: 1px solid var(--border);
  padding: clamp(1.25rem, 3vw, 2rem);
}
.hero__content { max-width: 920px; margin: 0 auto; text-align: left; }
.hero h1 { margin: 0; font-size: clamp(1.6rem, 1.8rem + 1vw, 2.3rem); line-height: 1.15; }
.sub { margin: .5rem 0 0; color: var(--muted); max-width: 65ch; }
.cta { display: flex; gap: .6rem; flex-wrap: wrap; margin-top: .9rem; }

.hero__art {
  position: absolute; inset: -20% -10% auto auto; height: 260px; width: 520px;
  background:
    radial-gradient(280px 180px at 70% 30%, rgba(44,128,180,.16), transparent 60%),
    radial-gradient(240px 160px at 30% 70%, rgba(53,73,94,.12), transparent 60%);
  pointer-events: none;
}

/* GRID */
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

/* CARDS */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
  text-align: left;
}
.card h2, .card h3 { margin: 0 0 .45rem; }
.muted { color: var(--muted); }

/* Lists */
.list { margin: .25rem 0 0; padding-left: 1.1rem; }
.list li { margin: .15rem 0; }
.steps { margin: .25rem 0 0; padding-left: 1.2rem; }
.steps li { margin: .25rem 0; }

/* Callouts */
.callout {
  margin-top: .6rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: .6rem .75rem;
}
.callout--warn { background: #fff3f2; border-color: #f5c2c0; color: #7d2d2a; }
.callout--tip  { background: #f2f9ff; border-color: #cfe6fb; color: #0b63a9; }

/* Samples */
.samples code {
  word-break: break-all;   /* allows wrapping mid-word */
  white-space: normal;     /* allows line breaks */
  font-size: 0.85rem;      /* optional: make it slightly smaller */
}
.samples {
  overflow-wrap: anywhere; /* extra safety for long strings */
}

.samples .label { font-weight: 700; margin-right: .35rem; }

/* Flags */
.flags { list-style: none; margin: 0; padding: 0; display: grid; gap: .5rem; }
.flag { width: 18px; vertical-align: middle; margin-right: 8px; }

/* CTA card */
.card--cta .card__row {
  display: grid; grid-template-columns: 1fr auto; gap: .75rem; align-items: center;
}

/* Buttons */
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
  padding: .8rem 1rem; border-radius: 12px; font-weight: 700; text-decoration: none;
  border: 1px solid transparent; transition: transform 60ms ease, filter 140ms ease, box-shadow 140ms ease;
}
.btn:focus-visible { outline: 3px solid rgba(44,128,180,.35); outline-offset: 2px; }
.btn--primary {
  background: linear-gradient(180deg, var(--accent-2), #1f6d9a);
  color: #fff; box-shadow: 0 6px 16px rgba(31,109,154,.18);
}
.btn--primary:hover { filter: brightness(1.05); }
.btn--ghost {
  background: #fff; color: var(--accent); border-color: var(--border);
}
.btn--ghost:hover { filter: brightness(0.98); }

/* Responsive */
@media (max-width: 960px) {
  .grid { grid-template-columns: 1fr; }
}
@media (max-width: 520px) {
  .hero__art { display: none; }
  .card--cta .card__row { grid-template-columns: 1fr; }
}
</style>

<!-- Route-scoped background & navbar layering (no App.vue changes) -->
<style>
.route-guide #app > nav { z-index: 1000; }
.route-guide,
.route-guide #app,
.route-guide #app > main { background: #f7f9fc; }
.route-guide .guide .hero,
.route-guide .guide .hero__content { position: relative; z-index: 0; }
.route-guide .guide .hero__art { position: absolute; z-index: -1; }
</style>
